import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import LocationPermissionModal from "./components/LocationPermissionModal";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Hide Emergent badge on auth pages and for authenticated users
const BADGE_SELECTORS = [
  '#emergent-badge',
  '[data-emergent-badge]',
  '[class*="emergent"]',
  'a[href*="emergentagent.com"]',
  'div[style*="position: fixed"][style*="bottom"]',
  '[class*="secured"]',
  '[class*="badge"]'
];

const BADGE_KEYWORDS = ['emergent', 'made with', 'secured by', 'powered by'];

function removeBadgeElements() {
  BADGE_SELECTORS.forEach(selector => {
    try {
      document.querySelectorAll(selector).forEach(el => {
        const text = (el.textContent || '').toLowerCase();
        const isBadge = BADGE_KEYWORDS.some(kw => text.includes(kw)) || selector === '#emergent-badge';
        if (isBadge) el.remove();
      });
    } catch (_) { /* invalid selector */ }
  });

  document.querySelectorAll('*').forEach(el => {
    const style = window.getComputedStyle(el);
    const text = (el.textContent || '').toLowerCase();
    if (style.position === 'fixed' && parseInt(style.bottom) < 50 &&
        (text.includes('emergent') || text.includes('secured'))) {
      el.remove();
    }
  });
}

function EmergentBadgeHider() {
  const { isAuthenticated } = useAuth();
  const location = window.location.pathname;
  
  const shouldHideBadge = isAuthenticated || 
    ['/login', '/register', '/forgot-password', '/staff-login'].some(p => location.includes(p));
  
  useEffect(() => {
    if (!shouldHideBadge) return;

    removeBadgeElements();
    const t1 = setTimeout(removeBadgeElements, 500);
    const t2 = setTimeout(removeBadgeElements, 1500);

    const observer = new MutationObserver(() => removeBadgeElements());
    observer.observe(document.body, { childList: true, subtree: true });

    const interval = setInterval(removeBadgeElements, 2000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [shouldHideBadge]);

  return null;
}

// Location prompt wrapper - needs to be inside AuthProvider
function LocationPromptWrapper() {
  const { showLocationPrompt, closeLocationPrompt } = useAuth();
  
  return (
    <LocationPermissionModal 
      isOpen={showLocationPrompt} 
      onClose={closeLocationPrompt}
    />
  );
}

// Pages
import Landing from "./pages/Landing";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import PaymentCallback from "./pages/PaymentCallback";
import StripeSuccess from "./pages/Payment/StripeSuccess";
import SubscriptionCallback from "./pages/SubscriptionCallback";
import Login from "./pages/Auth/Login";
import Register from "./pages/Auth/Register";
import StaffLogin from "./pages/Auth/StaffLogin";
import AuthCallback from "./pages/Auth/AuthCallback";
import ForgotPasswordSMS from "./pages/Auth/ForgotPasswordSMS";
import ResetPassword from "./pages/Auth/ResetPassword";
import Dashboard from "./pages/Dashboard/Dashboard";
import TicketView from "./pages/Dashboard/TicketView";
import CreateEvent from "./pages/Dashboard/CreateEvent";
import SubscriptionPlans from "./pages/Dashboard/SubscriptionPlans";
import OrganizerAnalytics from "./pages/Dashboard/OrganizerAnalytics";
import ProfileSettings from "./pages/Dashboard/ProfileSettings";
import Earnings from "./pages/Dashboard/Earnings";
import QRScanner from "./pages/Dashboard/QRScanner";
import PublicScanner from "./pages/PublicScanner";
import ReferralDashboard from "./pages/Dashboard/ReferralDashboard";
import BroadcastMessages from "./pages/Dashboard/BroadcastMessages";
import Unsubscribe from "./pages/Unsubscribe";
import AffiliateDashboardRedirect from "./pages/AffiliateDashboardRedirect";
import AdminPanel from "./pages/Admin/AdminPanel";
import MediaManagement from "./pages/Admin/MediaManagement";
import TwoFactorSetup from "./pages/Admin/TwoFactorSetup";
import AdminUsers from "./pages/Admin/AdminUsers";
import AdminManagement from "./pages/Admin/AdminManagement";
import LandingPageControl from "./pages/Admin/LandingPageControl";
import SupportDashboard from "./pages/Admin/SupportDashboard";
import ModeratorDashboard from "./pages/Admin/ModeratorDashboard";
import AdminAnalytics from "./pages/Admin/AdminAnalytics";
import MyTickets from "./pages/MyTickets";
import GuestTickets from "./pages/GuestTickets";
import GuestCheckout from "./pages/GuestCheckout";
import PastEvents from "./pages/PastEvents";
import Settings from "./pages/Settings";
import S3Migration from "./pages/Admin/S3Migration";
import SupportTickets from "./pages/Support/SupportTickets";
import About from "./pages/About";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import FAQ from "./pages/FAQ";
import RefundPolicy from "./pages/RefundPolicy";
import Organizers from "./pages/Organizers";

function AppRouter() {
  const location = useLocation();

  // Check for OAuth callback with session_id in URL fragment
  // This must be checked synchronously during render, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/organizers" element={<Organizers />} />
      <Route path="/events" element={<Events />} />
      <Route path="/events/past" element={<PastEvents />} />
      <Route path="/events/:eventId" element={<EventDetail />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/staff/login" element={<StaffLogin />} />
      <Route path="/staff/setup-2fa" element={<TwoFactorSetup />} />
      <Route path="/forgot-password" element={<ForgotPasswordSMS />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/payment/stripe/success" element={<StripeSuccess />} />
      <Route path="/subscription/callback" element={<SubscriptionCallback />} />

      {/* Regular user routes - no dashboard access */}
      <Route
        path="/my-tickets"
        element={
          <ProtectedRoute>
            <MyTickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Guest Tickets - Public Route (no auth required) */}
      <Route path="/my-tickets/guest" element={<GuestTickets />} />
      
      {/* Guest Checkout - Public Route (no auth required) */}
      <Route path="/checkout/guest/:eventId" element={<GuestCheckout />} />

      {/* Public QR Scanner - No login required, uses scan key */}
      <Route path="/scan/public/:scanKey" element={<PublicScanner />} />

      {/* Protected Routes - Dashboard for organizers and admins only */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/tickets"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/tickets/:ticketId"
        element={
          <ProtectedRoute>
            <TicketView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/events/create"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <CreateEvent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/events/:eventId/edit"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <CreateEvent />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/subscription"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <SubscriptionPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <ProfileSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/analytics"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <OrganizerAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/earnings"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <Earnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/scanner"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <QRScanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/referrals"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <ReferralDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/broadcasts"
        element={
          <ProtectedRoute requiredRoles={['organizer', 'admin']}>
            <BroadcastMessages />
          </ProtectedRoute>
        }
      />
      <Route path="/broadcasts/unsubscribe" element={<Unsubscribe />} />
      <Route path="/affiliate/dashboard" element={<AffiliateDashboardRedirect />} />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportTickets />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin']}>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/media"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin', 'moderator']}>
            <MediaManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <TwoFactorSetup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin', 'support']}>
            <SupportDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/management"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin']}>
            <AdminManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/landing-control"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin']}>
            <LandingPageControl />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin']}>
            <AdminAnalytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/moderator"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin', 'moderator']}>
            <ModeratorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/s3-migration"
        element={
          <ProtectedRoute requiredRoles={['admin']} requiredAdminRoles={['super_admin']}>
            <S3Migration />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

import { ErrorBoundary } from "./components/ErrorBoundary";

function App() {
  return (
    <div className="dark">
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <AppRouter />
            <LocationPromptWrapper />
            <EmergentBadgeHider />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
